import React, { useState, useEffect } from 'react';
import './DeviceForm.css';
import '../styles/clean-dashboard.css';
import { getApiBaseUrl } from '../config/api';

const DeviceForm = ({ device, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    display_name: '',
    phone_number: '',
    api_key: '',
    webhook_url: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (device) {
      setFormData({
        display_name: device.display_name || '',
        phone_number: device.phone_number || '',
        api_key: device.api_key || '',
        webhook_url: device.webhook_url || ''
      });
    }
  }, [device]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const generateApiKey = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let i = 0; i < 32; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setFormData(prev => ({
      ...prev,
      api_key: result
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('auth_token');
      const url = device 
        ? getApiBaseUrl(`/instances/${device.id}`)
        : getApiBaseUrl('/instances');
      
      const method = device ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (data.success) {
        onSave();
      } else {
        setError(data.error || 'Failed to save device');
      }
    } catch (error) {
      setError('Connection error: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="device-form-overlay">
      <div className="device-form-modal">
        <div className="device-form-header">
          <h3>{device ? 'Edit Device' : 'Add New Device'}</h3>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <form onSubmit={handleSubmit} className="device-form">
          <div className="form-group">
            <label htmlFor="display_name">Device Name *</label>
            <input
              type="text"
              id="display_name"
              name="display_name"
              value={formData.display_name}
              onChange={handleInputChange}
              placeholder="e.g., Main WhatsApp, Support Bot"
              required
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="phone_number">Phone Number</label>
            <input
              type="tel"
              id="phone_number"
              name="phone_number"
              value={formData.phone_number}
              onChange={handleInputChange}
              placeholder="e.g., +1234567890"
              disabled={loading}
            />
            <small>Will be auto-filled after connection</small>
          </div>

          <div className="form-group">
            <label htmlFor="api_key">API Key</label>
            <div className="api-key-input">
              <input
                type="text"
                id="api_key"
                name="api_key"
                value={formData.api_key}
                onChange={handleInputChange}
                placeholder="Auto-generated or custom API key"
                disabled={loading}
              />
              <button
                type="button"
                className="generate-btn"
                onClick={generateApiKey}
                disabled={loading}
              >
                Generate
              </button>
            </div>
            <small>Used for external API access</small>
          </div>

          <div className="form-group">
            <label htmlFor="webhook_url">Webhook URL</label>
            <input
              type="url"
              id="webhook_url"
              name="webhook_url"
              value={formData.webhook_url}
              onChange={handleInputChange}
              placeholder="https://your-app.com/webhook"
              disabled={loading}
            />
            <small>URL to receive incoming messages (optional)</small>
          </div>

          {error && <div className="error-message">{error}</div>}

          <div className="form-actions">
            <button
              type="button"
              className="cancel-btn"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="save-btn"
              disabled={loading || !formData.display_name.trim()}
            >
              {loading ? 'Saving...' : (device ? 'Update Device' : 'Add Device')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DeviceForm;
